import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// Shared portfolio pieces: the project tile and the full-screen project viewer.
// Used by the 3D Services portfolio and the homepage "Featured Projects".
// A project: { id, name, category, cover, media: [{ type: "image" | "video", src, thumbnail?, platform? }], link? }
// link: { to, label } for a page on this site, or { href, label, icon? } for an external page

export const getYoutubeId = (url = "") => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return match?.[1] || "";
};

export const getYoutubeEmbedUrl = (url = "") => {
  const id = getYoutubeId(url);
  return id ? `https://www.youtube.com/embed/${id}` : "";
};

export const getYoutubeThumbnail = (url = "") => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
};

const hasVideo = (project) => project.media?.some((media) => media.type === "video");

// Videos first, then images
const orderMedia = (media = []) => [...media.filter((m) => m.type === "video"), ...media.filter((m) => m.type !== "video")];

export const ProjectTile = ({ project, cover, onOpen, className = "", children }) => (
  <button
    className={`services-portfolio__card${project.tileSize && project.tileSize !== "standard" ? ` services-portfolio__card--${project.tileSize}` : ""} ${className}`}
    type="button"
    onClick={onOpen}
  >
    <img src={cover || project.cover} alt={`${project.name} portfolio cover`} loading="lazy" />
    {hasVideo(project) && (
      <span className="services-portfolio__badge">
        <i className="fa-solid fa-play"></i> Video
      </span>
    )}
    <span className="services-portfolio__overlay">
      <strong>{project.name}</strong>
      <em>{project.category}</em>
      {children}
    </span>
  </button>
);

export const ProjectViewer = ({ projects, index, onIndexChange, onClose, initialMedia = 0 }) => {
  const project = projects[index];
  const media = orderMedia(project?.media);
  const [mediaIndex, setMediaIndex] = useState(initialMedia);
  const shownProject = useRef(project?.id);
  const [dragStartX, setDragStartX] = useState(null);
  const activeMedia = media[mediaIndex];

  // Each project opens on its first item (a video when it has one); the first project
  // opens on `initialMedia`
  useEffect(() => {
    if (shownProject.current === project?.id) return;
    shownProject.current = project?.id;
    setMediaIndex(0);
  }, [project?.id]);

  const stepMedia = (direction) => {
    if (media.length < 2) return;
    setMediaIndex((current) => (current + (direction === "next" ? 1 : -1) + media.length) % media.length);
  };

  const switchProject = (direction) => {
    const total = projects.length;
    onIndexChange(direction === "next" ? (index + 1) % total : (index - 1 + total) % total);
  };

  // Arrows, keys and swipes move through the project's media; single-item projects
  // (e.g. a gallery of images) move to the next project instead
  const singleMedia = media.length < 2;
  const step = (direction) => (singleMedia ? switchProject(direction) : stepMedia(direction));
  const showNav = !singleMedia || projects.length > 1;

  useEffect(() => {
    document.body.classList.add("services-portfolio-modal-open");
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") step("next");
      if (event.key === "ArrowLeft") step("prev");
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("services-portfolio-modal-open");
      window.removeEventListener("keydown", onKey);
    };
  });

  const handleDragEnd = (clientX) => {
    if (dragStartX === null) return;
    const distance = clientX - dragStartX;
    setDragStartX(null);
    if (Math.abs(distance) < 60) return;
    step(distance < 0 ? "next" : "prev");
  };

  if (!project) return null;

  const renderStage = () => {
    if (!activeMedia) return null;
    if (activeMedia.type !== "video") {
      return <img src={activeMedia.src} alt={activeMedia.alt || project.name} />;
    }
    if (activeMedia.platform === "file") {
      return <video key={activeMedia.src} src={activeMedia.src} poster={activeMedia.thumbnail} controls autoPlay muted loop playsInline />;
    }
    const embed = getYoutubeEmbedUrl(activeMedia.src);
    return embed ? (
      <iframe
        key={embed}
        className={activeMedia.vertical ? "is-vertical" : undefined}
        src={`${embed}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
        title={`${project.name} video`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      ></iframe>
    ) : (
      <div className="pfv__external">
        <p>{activeMedia.platform === "instagram" ? "Instagram video" : "Project video"}</p>
        <a href={activeMedia.src} target="_blank" rel="noreferrer">
          Open video <i className="fa-regular fa-arrow-up-right"></i>
        </a>
      </div>
    );
  };

  return (
    <div className="pfv" role="dialog" aria-modal="true" aria-label={`${project.name} project`}>
      <header className="pfv__bar">
        <div className="pfv__title">
          <span>{project.category}</span>
          <h3>{project.name}</h3>
        </div>
        <div className="pfv__actions">
          {project.link?.to && (
            <Link className="pfv__link" to={project.link.to} onClick={onClose}>
              {project.link.label} <i className="fa-regular fa-arrow-right"></i>
            </Link>
          )}
          {project.link?.href && (
            <a className="pfv__link" href={project.link.href} target="_blank" rel="noreferrer">
              {project.link.icon && <i className={project.link.icon}></i>} {project.link.label} <i className="fa-regular fa-arrow-up-right"></i>
            </a>
          )}
          <span className="pfv__count">
            {index + 1} / {projects.length}
          </span>
          {projects.length > 1 && (
            <>
              <button type="button" onClick={() => switchProject("prev")} aria-label="Previous project">
                <i className="fa-regular fa-arrow-left"></i>
              </button>
              <button type="button" onClick={() => switchProject("next")} aria-label="Next project">
                <i className="fa-regular fa-arrow-right"></i>
              </button>
            </>
          )}
          <button type="button" className="pfv__close" onClick={onClose} aria-label="Close project">
            <i className="fa-regular fa-xmark"></i>
          </button>
        </div>
      </header>

      <div
        className="pfv__stage"
        onPointerDown={(event) => event.pointerType !== "mouse" && setDragStartX(event.clientX)}
        onPointerUp={(event) => handleDragEnd(event.clientX)}
      >
        <div className={`pfv__media${activeMedia?.type === "video" ? " is-video" : ""}`}>{renderStage()}</div>
        {showNav && (
          <>
            <button type="button" className="pfv__nav pfv__nav--prev" onClick={() => step("prev")} aria-label={singleMedia ? "Previous project" : "Previous media"}>
              <i className="fa-regular fa-chevron-left"></i>
            </button>
            <button type="button" className="pfv__nav pfv__nav--next" onClick={() => step("next")} aria-label={singleMedia ? "Next project" : "Next media"}>
              <i className="fa-regular fa-chevron-right"></i>
            </button>
          </>
        )}
      </div>

      {media.length > 1 && (
        <div className="pfv__strip" aria-label="Project media">
          {media.map((item, i) => (
            <button
              type="button"
              key={`${project.id}-${i}`}
              className={mediaIndex === i ? "is-active" : ""}
              onClick={() => setMediaIndex(i)}
              aria-label={`${item.type === "video" ? "Video" : "Image"} ${i + 1} of ${media.length}`}
            >
              {item.type === "video" ? (
                <>
                  {(item.thumbnail || getYoutubeThumbnail(item.src)) && (
                    <img src={item.thumbnail || getYoutubeThumbnail(item.src)} alt="" loading="lazy" />
                  )}
                  <span className="pfv__play">
                    <i className="fa-solid fa-play"></i>
                  </span>
                </>
              ) : (
                <img src={item.src} alt="" loading="lazy" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
